import { useLayoutEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { ddPanelSubAccountsAPI } from '../../api/services/dd_panel_sub_accounts'
import { InvoicesTypes } from '../../api/services/invoice/types.ts'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Button from '../../components/common/Button/Button.tsx'
import Card from '../../components/common/Card/Card.tsx'
import Loader from '../../components/common/Loader/Loader.tsx'
import Table from '../../components/common/Table/Table.tsx'
import EppSection from '../../components/invoices/EppSection.tsx'
import { useAuth } from '../../context/auth'
import { useInvoices } from '../../hooks/useInvoices.ts'
import {
  setInvoicesTableActions,
  setInvoicesTableColumns
} from '../../utils/invoices/invoicesTableSettings.tsx'
import { isUserExistsInGroup } from '../../utils/userRights.ts'

export const Invoices = () => {
  const { state } = useAuth()
  const hasMainUserRights = isUserExistsInGroup(
    state.groups,
    'dd_panel_main_users'
  )
  const { invoices, loading } = useInvoices()

  const [iframeUrl, setIframeUrl] = useState<string | undefined| null>(undefined)
  const [gettingLink, setGettingLink] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const [collapsedNewInvoices, setCollapsedNewInvoices] =
    useState<boolean>(false)
  const [collapsedOldInvoices, setCollapsedOldInvoices] =
    useState<boolean>(!hasMainUserRights)

  const columns = setInvoicesTableColumns()
  const actions = setInvoicesTableActions()

  const description =
    'Faktury wystawione po 1.04.2026 będą dostępne tylko\n' +
    '                poprzez panel programu Fakturownia.'

  useLayoutEffect(() => {
    !hasMainUserRights && onClickFakturownia()
  }, [])

  const onClickFakturownia = async () => {
    try {
      setGettingLink(true)
      const response = await ddPanelSubAccountsAPI.getFakturowniaPanelLink()
      if (response.status === 200) {
        response.data.link && setIframeUrl(response.data.link)
      } else if (response.status === 400) {
        setError(response.error)
      } else {
        toast('Błąd podczas pobierania linku do panelu Fakturownia')
      }
    } finally {
      setGettingLink(false)
    }
  }

  return (
    <>
      <Breadcrumbs pageName="Faktury" breadcrumbs={[]} />
      <Card className="mb-4">
        {!hasMainUserRights && !error && (
          <Card.Title>
            <div
              className={
                'flex flex-col 2xl:flex-row gap-4 2xl:gap-0 items-center'
              }
            >
              <div className={'flex flex-col'}>
                <span className="text-red text-2xl font-bold py-2 mr-4 text-center 2xl:text-left">
                  Uwaga!
                </span>
                <span className={'text-[18px] text-gray-500'}>
                  {description}
                </span>
              </div>
              {iframeUrl && (
                <div className={'flex-1 flex justify-end'}>
                  <Button
                    variant="filled"
                    size="medium"
                    color="primary"
                    onClick={() =>
                      setCollapsedNewInvoices(!collapsedNewInvoices)
                    }
                  >
                    {collapsedNewInvoices
                      ? 'Pokaż Fakturownia'
                      : 'Ukryj Fakturownia'}
                  </Button>
                </div>
              )}
            </div>
          </Card.Title>
        )}
        {!collapsedNewInvoices && !hasMainUserRights && !error &&(
          <Card.Body>
            {!iframeUrl === undefined && !gettingLink && (
              <div className="text-red text-center py-2">
                Jeżeli nie widzisz w tym miejscu panelu Fakturownia z
                dokumentami to zgłoś to nam.
              </div>
            )}
            {!iframeUrl && gettingLink && (
              <div className="text-center py-2">
                <Loader label={'Pobieranie danych...'} />
              </div>
            )}
            {iframeUrl && (
              <iframe
                src={iframeUrl}
                title="Zewnętrzna strona"
                width="100%"
                height="600"
              />
            )}
          </Card.Body>
        )}
      </Card>

      {invoices !== null &&
        (( !hasMainUserRights) ||
          hasMainUserRights) && (
          <Card>
            <Card.Body>
              <div className="flex justify-end items-center">
                {!hasMainUserRights && (
                  <Button
                    variant="filled"
                    size="medium"
                    color="primary"
                    onClick={() =>
                      setCollapsedOldInvoices(!collapsedOldInvoices)
                    }
                  >
                    {collapsedOldInvoices
                      ? 'Pokaż wcześniejsze faktury'
                      : 'Ukryj wcześniejsze faktury'}
                  </Button>
                )}
              </div>

              {loading && (
                <Loader label={'Pobieranie danych o transakcjach...'} />
              )}
              {!collapsedOldInvoices && !loading && invoices.length == 0 && (
                <div className="flex justify-center min-w-200 overflow-y-auto border border-gray-300 dark:border-gray-600 my-4 p-20">
                  <p className={'text-[24px]'}>Brak wygenerowanych faktur</p>
                </div>
              )}
              {!collapsedOldInvoices && !loading && invoices.length !== 0 && (
                <div className="min-w-200 h-180 overflow-y-auto border border-gray-300 dark:border-gray-600 my-4 p-4">
                  {hasMainUserRights && <EppSection />}
                  <Table
                    columns={columns}
                    data={invoices as InvoicesTypes[]}
                    actions={actions}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        )}
    </>
  )
}
